// 10 种 condition type 的 zod discriminated union。
// 对应 daemon/internal/web/game_profile.go structuredConditionsFromRequest 的 switch。
// 序列化输出会作为 enterConditions 字段传给 daemon（已经是 JSON）。

import { z } from "zod";

const Op = z.enum([">", ">=", "<", "<=", "==", "!="]);

export const ConditionMinLevel = z.object({
  type: z.literal("min_level"),
  value: z.coerce.number().int().nonnegative(),
});

export const ConditionMinReincarnation = z.object({
  type: z.literal("min_reincarnation"),
  value: z.coerce.number().int().nonnegative(),
});

export const ConditionMinEvolutionLevel = z.object({
  type: z.literal("min_evolution_level"),
  value: z.coerce.number().int().nonnegative(),
});

export const ConditionServerOpenDays = z.object({
  type: z.literal("server_open_days"),
  op: Op,
  value: z.coerce.number().int().nonnegative(),
});

export const ConditionVipLevel = z.object({
  type: z.literal("vip_level"),
  op: Op,
  value: z.coerce.number().int().nonnegative(),
});

export const ConditionCombatPower = z.object({
  type: z.literal("combat_power"),
  op: Op,
  value: z.coerce.number().int().nonnegative(),
});

export const ConditionItemCount = z.object({
  type: z.literal("item_count"),
  item_id: z.string().optional().default(""),
  item_name: z.string().optional().default(""),
  op: Op,
  count: z.coerce.number().int().nonnegative(),
});

export const ConditionEquipmentMultiplier = z.object({
  type: z.literal("equipment_multiplier"),
  item_id: z.string().optional().default(""),
  item_name: z.string().optional().default("刀刀切割"),
  op: Op,
  value: z.coerce.number().int().nonnegative(),
});

export const ConditionCurrencyBalance = z.object({
  type: z.literal("currency_balance"),
  currency: z.string().min(1),
  op: Op,
  amount: z.coerce.number().int().nonnegative(),
});

export const ConditionCurrencyCost = z.object({
  type: z.literal("currency_cost"),
  currency: z.string().min(1),
  amount: z.coerce.number().int().nonnegative(),
});

export const ConditionSchema = z.discriminatedUnion("type", [
  ConditionMinLevel,
  ConditionMinReincarnation,
  ConditionMinEvolutionLevel,
  ConditionServerOpenDays,
  ConditionVipLevel,
  ConditionCombatPower,
  ConditionItemCount,
  ConditionEquipmentMultiplier,
  ConditionCurrencyBalance,
  ConditionCurrencyCost,
]);
export type Condition = z.infer<typeof ConditionSchema>;
export type ConditionType = Condition["type"];

export const CONDITION_TYPE_LABELS: Record<ConditionType, string> = {
  min_level: "最低等级",
  min_reincarnation: "最低转生",
  min_evolution_level: "最低进化",
  server_open_days: "开服天数",
  vip_level: "VIP等级",
  combat_power: "战斗力",
  item_count: "物品数量",
  equipment_multiplier: "装备倍数",
  currency_balance: "货币余额",
  currency_cost: "货币消耗",
};

export const CONDITION_OPS: Array<{ value: z.infer<typeof Op>; label: string }> = [
  { value: ">", label: ">" },
  { value: ">=", label: "≥" },
  { value: "<", label: "<" },
  { value: "<=", label: "≤" },
  { value: "==", label: "==" },
  { value: "!=", label: "≠" },
];

/** 为新插入的 condition 提供合理默认。 */
export function defaultsForType(type: ConditionType): Condition {
  switch (type) {
    case "min_level":
      return { type, value: 1 };
    case "min_reincarnation":
      return { type, value: 0 };
    case "min_evolution_level":
      return { type, value: 1 };
    case "server_open_days":
      return { type, op: ">=", value: 1 };
    case "vip_level":
      return { type, op: ">=", value: 1 };
    case "combat_power":
      return { type, op: ">=", value: 0 };
    case "item_count":
      return { type, item_id: "", item_name: "", op: ">=", count: 1 };
    case "equipment_multiplier":
      return { type, item_id: "", item_name: "刀刀切割", op: ">=", value: 1 };
    case "currency_balance":
      return { type, currency: "", op: ">=", amount: 0 };
    case "currency_cost":
      return { type, currency: "", amount: 0 };
  }
}
